// The two license screens (D-017): sign-in for new installs, and the
// lapse screen — which, per the D-017 rider, must ALWAYS leave the
// trader's own data reachable: read-only journal + one-file export.
// Visual language: calm, centered, no shame.

import { el } from "./dom.js";
import { login, recoverKey, requestMagicLink, type LicenseState } from "./license.js";
import { platform } from "./platform/index.js";
import { exportBackup } from "./backup.js";
import { journalStore } from "./journal-store.js";

function fmtR(value: number): string {
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}R`;
}

export function renderLicenseGate(root: HTMLElement, state: Exclude<LicenseState, "active">, onUnlocked: () => void): void {
  const container = el("div", {
    style: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      height: "100%",
      textAlign: "center",
      padding: "0 16px",
      overflow: "auto",
    },
  });
  root.replaceChildren(container);

  // Magic-link sign-in (D-151) is the friendly default on the web app. In the
  // extension a web link can't sign you in, so it keeps email + key.
  const isWeb = platform.kind === "web";

  const title = el("div", {
    text: state === "lapsed" ? "Your subscription has ended" : "Sign in to TickAhead",
    style: { font: "var(--font-header)" },
  });
  container.appendChild(title);

  container.appendChild(
    el("div", {
      text:
        state === "lapsed"
          ? "Renew at tickahead.com and sign back in. Your journal below stays yours either way — readable here, exportable to one file, forever."
          : isWeb
            ? "Enter the email you subscribed with and we'll send you a one-tap sign-in link. Prefer a license key? You can use that instead."
            : "Your license key is on your welcome page and in your purchase email. Don't have one yet? tickahead.com has the details.",
      style: { font: "var(--font-body)", color: "var(--text-secondary)", maxWidth: "260px" },
    })
  );

  const buyLink = el("a", {
    text: state === "lapsed" ? "Renew at tickahead.com" : "Get TickAhead at tickahead.com",
    attrs: { href: "https://tickahead.com", target: "_blank", rel: "noopener" },
  });
  Object.assign(buyLink.style, { font: "var(--font-body)", color: "var(--accent-blue)" });
  container.appendChild(buyLink);

  // Sign-in form (also the path back in after renewing).
  const form = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px", width: "240px" } });
  const emailInput = el("input", { attrs: { type: "email", placeholder: "Email", autocomplete: "email" } }) as HTMLInputElement;
  const keyInput = el("input", { attrs: { type: "text", placeholder: "License key (TA-…)" } }) as HTMLInputElement;
  const errorLine = el("div", { style: { font: "var(--font-caption)", color: "var(--down)", minHeight: "14px" } });
  const signInBtn = el("button", { className: "pill", text: "Sign in" });
  signInBtn.addEventListener("click", () => {
    errorLine.textContent = "";
    signInBtn.textContent = "Signing in…";
    (signInBtn as HTMLButtonElement).disabled = true;
    void login(emailInput.value, keyInput.value).then((result) => {
      if (result.ok) {
        onUnlocked();
        return;
      }
      errorLine.style.color = "var(--down)";
      errorLine.textContent = result.message ?? "Sign-in failed.";
      signInBtn.textContent = "Sign in";
      (signInBtn as HTMLButtonElement).disabled = false;
    });
  });
  keyInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") signInBtn.click();
  });

  // The key sub-form: the only form in the extension; a revealed fallback on web.
  const keyGroup = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px" } });
  keyGroup.append(keyInput, signInBtn);

  // Forgot-your-key recovery (D-144) + can't-find-email support — belong with the
  // key path, so they live in a group shown alongside the key form.
  const forgot = el("a", {
    text: "Forgot your key?",
    attrs: { href: "#", role: "button" },
    style: { font: "var(--font-caption)", color: "var(--accent-blue)", cursor: "pointer" },
  });
  forgot.addEventListener("click", (e) => {
    e.preventDefault();
    errorLine.textContent = "";
    if (!emailInput.value.trim()) {
      emailInput.focus();
      errorLine.style.color = "var(--text-secondary)";
      errorLine.textContent = "Enter your email above first, then tap Forgot your key.";
      return;
    }
    forgot.textContent = "Sending…";
    void recoverKey(emailInput.value).then((result) => {
      errorLine.style.color = result.ok ? "var(--text-secondary)" : "var(--down)";
      errorLine.textContent = result.message;
      forgot.textContent = "Forgot your key?";
    });
  });
  const support = el("div", {
    style: { font: "var(--font-caption)", color: "var(--text-secondary)", maxWidth: "240px", marginTop: "2px", textAlign: "center" },
  });
  support.append("Can't find the email you bought with? ");
  support.appendChild(
    el("a", {
      text: "Contact us",
      attrs: { href: "mailto:contact@tickahead.com?subject=Can%27t%20find%20my%20login", target: "_blank", rel: "noopener" },
      style: { color: "var(--accent-blue)" },
    })
  );
  const keyExtras = el("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" } });
  keyExtras.append(forgot, support);

  form.appendChild(emailInput);

  if (isWeb) {
    // Primary: email me a one-tap sign-in link.
    const magicBtn = el("button", { className: "pill", text: "Email me a sign-in link" });
    const setMagicBusy = (busy: boolean): void => {
      magicBtn.textContent = busy ? "Sending…" : "Email me a sign-in link";
      (magicBtn as HTMLButtonElement).disabled = busy;
    };
    magicBtn.addEventListener("click", () => {
      errorLine.textContent = "";
      setMagicBusy(true);
      void requestMagicLink(emailInput.value).then((result) => {
        errorLine.style.color = result.ok ? "var(--text-secondary)" : "var(--down)";
        errorLine.textContent = result.message;
        setMagicBusy(false);
      });
    });
    // Fallback: reveal the license-key form.
    const keyToggle = el("a", {
      text: "Have a license key? Sign in with it",
      attrs: { href: "#", role: "button" },
      style: { font: "var(--font-caption)", color: "var(--accent-blue)", cursor: "pointer" },
    });
    keyGroup.style.display = "none";
    keyExtras.style.display = "none";
    keyToggle.addEventListener("click", (e) => {
      e.preventDefault();
      keyGroup.style.display = "flex";
      keyExtras.style.display = "flex";
      magicBtn.style.display = "none";
      keyToggle.style.display = "none";
      keyInput.focus();
    });
    emailInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && keyGroup.style.display === "none") magicBtn.click();
    });
    form.append(magicBtn, keyToggle, keyGroup, errorLine);
  } else {
    form.append(keyGroup, errorLine);
  }
  container.appendChild(form);
  container.appendChild(keyExtras);

  // The rider: data access survives the paywall, unconditionally.
  if (state === "lapsed") {
    container.appendChild(
      el("button", {
        className: "pill",
        text: "Export all my data",
        onClick: () => void exportBackup(),
      })
    );
    void journalStore.init().then(() => {
      if (!journalStore.entries.length) return;
      container.appendChild(
        el("div", {
          text: "Your journal (read-only)",
          style: { font: "var(--font-label)", color: "var(--text-secondary)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "6px" },
        })
      );
      const list = el("div", {
        className: "scroll",
        style: { maxHeight: "180px", width: "260px", display: "flex", flexDirection: "column", gap: "4px", textAlign: "left" },
      });
      for (const entry of journalStore.entries) {
        const date = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(entry.date));
        const row = el("div", { style: { display: "flex", flexDirection: "column", gap: "1px", padding: "4px 0", borderBottom: "1px solid var(--border)" } });
        row.appendChild(
          el("div", { style: { display: "flex", gap: "8px" } }, [
            el("div", { text: entry.symbol || "—", style: { font: "var(--font-header)", flex: "1" } }),
            el("div", { text: fmtR(entry.rMultiple), style: { font: "var(--font-mono-value)", color: entry.rMultiple >= 0 ? "var(--up)" : "var(--down)" } }),
            el("div", { text: date, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
          ])
        );
        if (entry.lesson) {
          row.appendChild(el("div", { text: entry.lesson, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }));
        }
        list.appendChild(row);
      }
      container.appendChild(list);
    });
  }
}
